import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { AddItemsSheet } from '../components/AddItemsSheet';
import { BottomSheet } from '../components/BottomSheet';
import { BottomNav, SyncBadge } from '../components/Layout';
import { cardCls, inputCls, primaryBtn, secondaryBtn, sectionTitleCls } from '../components/ui/classes';
import { flashFrom } from '../components/ui/flash';
import { householdErrorMessage } from '../components/ui/householdErrors';
import { LiveDot } from '../components/ui/LiveDot';
import { PageHeader } from '../components/ui/PageHeader';
import { SheetAction } from '../components/ui/SheetAction';
import { SkeletonRows } from '../components/ui/Skeleton';
import { Toast, toast, type ToastMessage } from '../components/ui/Toast';
import { useLongPress } from '../components/ui/useLongPress';
import { declineInvite, dismissHouseholdNotice, joinHousehold, useHouseholdNotice, useMyInvites, type MyInvite } from '../data/household';
import { archiveList, deleteList, newListFrom, renameList, unarchiveList, useLists } from '../data/lists';
import { useShoppingNow, type ShoppingNow } from '../data/presence';
import { useStoreResolver, useStores } from '../data/stores';
import { db } from '../db/db';
import type { ListRow } from '../db/types';

interface Progress {
  done: number;
  total: number;
  missing: number;
}

/** Postęp widocznych list jednym zapytaniem (pozycje pogrupowane po liście). */
function useProgress(listIds: Id[]): Map<Id, Progress> | undefined {
  return useLiveQuery(async () => {
    const out = new Map<Id, Progress>();
    if (listIds.length === 0) return out;
    const items = await db.listItems.where('listId').anyOf(listIds).toArray();
    for (const i of items) {
      if (i.deleted) continue;
      const p = out.get(i.listId) ?? { done: 0, total: 0, missing: 0 };
      p.total++;
      if (i.status !== 'pending') p.done++;
      if (i.status === 'missing') p.missing++;
      out.set(i.listId, p);
    }
    return out;
  }, [listIds.join(',')]);
}

const isActive = (l: ListRow) => (l.status ?? 'active') === 'active';

const NO_SHOPPERS: ShoppingNow[] = [];

export function ListsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const lists = useLists();
  const stores = useStores();
  const resolveStore = useStoreResolver();
  const shopping = useShoppingNow();
  const invites = useMyInvites();
  const notice = useHouseholdNotice();
  const [message, setMessage] = useState<ToastMessage | null>(() => {
    const text = flashFrom(location.state);
    return text ? toast(text) : null;
  });
  // Komunikat z poprzedniego ekranu pokazujemy raz — nie po odświeżeniu strony.
  useEffect(() => {
    if (flashFrom(location.state)) navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);
  const [doneOpen, setDoneOpen] = useState<boolean | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Lista w menu zostaje w stanie po zamknięciu, żeby arkusz miał treść podczas animacji wyjścia.
  const [menuList, setMenuList] = useState<ListRow | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Lista do dopisywania zostaje po zamknięciu arkusza (animacja wyjścia i komunikat „Dopisano…”).
  const [addTo, setAddTo] = useState<Id | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const active = lists?.filter(isActive) ?? [];
  const done = lists?.filter((l) => l.status === 'done') ?? [];
  const archived = lists?.filter((l) => l.status === 'archived') ?? [];
  // Bez aktywnych list zakończone są rozwinięte, dopóki użytkownik sam nie zdecyduje.
  const showDone = doneOpen ?? active.length === 0;
  const visible = [...active, ...(showDone ? done : []), ...(showDone && archiveOpen ? archived : [])];
  const progress = useProgress(visible.map((l) => l.id));
  const storeName = new Map(stores?.map((s) => [s.id, s.name]) ?? []);
  // Lista może wskazywać na scalony duplikat sklepu.
  const nameOf = (id: Id | null) => (id ? (storeName.get(resolveStore ? resolveStore(id) : id) ?? null) : null);
  const shoppersByList = new Map<Id, ShoppingNow[]>();
  for (const s of shopping ?? []) shoppersByList.set(s.listId, [...(shoppersByList.get(s.listId) ?? []), s]);
  const othersShopping = shopping?.filter((s) => !s.isMe) ?? [];

  const openMenu = (l: ListRow) => {
    setMenuList(l);
    setMenuOpen(true);
  };

  const card = (l: ListRow) => (
    <ListCard
      key={l.id}
      list={l}
      storeName={nameOf(l.storeId)}
      progress={progress?.get(l.id) ?? (progress ? { done: 0, total: 0, missing: 0 } : undefined)}
      shoppers={shoppersByList.get(l.id) ?? NO_SHOPPERS}
      onMenu={() => openMenu(l)}
    />
  );

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="size-7" />
            Odhaczka
          </span>
        }
        right={<SyncBadge />}
      />
      <main className="flex flex-1 flex-col gap-6 p-4">
        {notice === 'removed' && <RemovedNotice />}
        {othersShopping.length > 0 && <ShoppingNowBar shoppers={othersShopping} />}
        {invites?.map((inv) => (
          <InviteBanner key={inv.id} invite={inv} onJoined={(text) => setMessage(toast(text))} />
        ))}
        {lists === undefined ? (
          <SkeletonRows />
        ) : lists.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <section className="flex flex-col gap-2">
              <h2 className={sectionTitleCls}>Aktywne</h2>
              {active.length > 0 ? (
                <ul className="flex flex-col gap-2">{active.map(card)}</ul>
              ) : (
                <div className={`${cardCls} flex flex-col gap-3 p-4`}>
                  <p className="text-stone-600 dark:text-stone-400">Nic do kupienia. Przyszedł SMS z listą?</p>
                  <Link to="/new" className={primaryBtn}>
                    Wklej listę zakupów
                  </Link>
                </div>
              )}
            </section>

            {(done.length > 0 || archived.length > 0) && (
              <section className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setDoneOpen(!showDone)}
                  aria-expanded={showDone}
                  className={`${sectionTitleCls} -my-2 flex min-h-11 items-center gap-1 text-left`}
                >
                  Zakończone
                  <span className="font-normal normal-case tracking-normal">({done.length})</span>
                  <span aria-hidden className={`ml-auto text-base transition-transform ${showDone ? 'rotate-180' : ''}`}>
                    ▾
                  </span>
                </button>
                {showDone && (
                  <>
                    {done.length > 0 && <ul className="flex flex-col gap-2">{done.map(card)}</ul>}
                    {archived.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setArchiveOpen(!archiveOpen)}
                        className="min-h-11 self-start px-1 text-sm font-medium text-brand-700 dark:text-brand-500"
                      >
                        {archiveOpen ? 'Ukryj archiwum' : `Pokaż archiwum (${archived.length})`}
                      </button>
                    )}
                    {archiveOpen && archived.length > 0 && <ul className="flex flex-col gap-2 opacity-75">{archived.map(card)}</ul>}
                  </>
                )}
              </section>
            )}
          </>
        )}
      </main>
      <BottomNav />

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title={menuList?.title || undefined}>
        {menuList && (
          <ListMenu
            list={menuList}
            onClose={() => setMenuOpen(false)}
            onAddItems={() => {
              setMenuOpen(false);
              setAddTo(menuList.id);
              setAddOpen(true);
            }}
          />
        )}
      </BottomSheet>
      {addTo && <AddItemsSheet listId={addTo} open={addOpen} onClose={() => setAddOpen(false)} />}
      <Toast message={message} onDismiss={() => setMessage(null)} />
    </>
  );
}

/** Po usunięciu z domu (przez kogoś innego) dane domu zostały wyczyszczone z telefonu. */
function RemovedNotice() {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-100">
      <p>Nie jesteś już we wspólnym domu — listy domu zniknęły z tego telefonu. Możesz poprosić o nowe zaproszenie.</p>
      <button
        type="button"
        onClick={() => void dismissHouseholdNotice()}
        className="min-h-11 self-end rounded-lg bg-amber-100 px-5 font-semibold active:bg-amber-200 dark:bg-amber-900/40 dark:active:bg-amber-900/60"
      >
        OK
      </button>
    </section>
  );
}

/** Inni domownicy w sklepie — tap otwiera ich listę. */
function ShoppingNowBar({ shoppers }: { shoppers: ShoppingNow[] }) {
  return (
    <section
      aria-label="Teraz na zakupach"
      className="divide-y divide-brand-100 overflow-hidden rounded-2xl bg-brand-50 text-brand-800 dark:divide-brand-800/40 dark:bg-brand-800/20 dark:text-brand-100"
    >
      {shoppers.map((s) => (
        <Link
          key={`${s.userId}|${s.listId}`}
          to={`/lists/${s.listId}`}
          className="flex min-h-12 items-center gap-3 px-4 py-2 active:bg-brand-100 dark:active:bg-brand-800/30"
        >
          <LiveDot />
          <span className="line-clamp-2 min-w-0 flex-1">
            Teraz na zakupach: <b className="font-semibold">{s.name}</b>
            {s.storeName && ` (${s.storeName})`}
          </span>
          <span aria-hidden className="text-xl leading-none opacity-60">
            ›
          </span>
        </Link>
      ))}
    </section>
  );
}

function InviteBanner({ invite, onJoined }: { invite: MyInvite; onJoined: (text: string) => void }) {
  const [busy, setBusy] = useState<'join' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const from = invite.invitedByName || 'Ktoś';

  const run = async (kind: 'join' | 'decline') => {
    if (kind === 'decline' && !confirm(`Odrzucić zaproszenie od ${from}?`)) return;
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'join') {
        await joinHousehold({ inviteId: invite.id });
        onJoined(`Jesteś teraz w domu „${invite.householdName}”.`);
      } else {
        await declineInvite(invite.id);
      }
    } catch (e) {
      setError(householdErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-4 shadow-sm dark:border-brand-800/40 dark:bg-stone-900">
      <div>
        <p className="font-semibold">
          {from} zaprasza Cię do domu „{invite.householdName}”
        </p>
        <p className="mt-1 text-sm text-stone-500">Wspólne listy i sklepy. Twoje dotychczasowe listy trafią do wspólnego domu.</p>
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={busy !== null} onClick={() => void run('decline')} className={secondaryBtn}>
          Odrzuć
        </button>
        <button type="button" disabled={busy !== null} onClick={() => void run('join')} className={primaryBtn}>
          {busy === 'join' ? 'Dołączam…' : 'Dołącz'}
        </button>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-2 text-center">
      <img src="/logo.png" alt="" className="size-20 opacity-90" />
      <div>
        <h2 className="text-xl font-semibold">Brak list zakupów</h2>
        <p className="mt-1 text-stone-600 dark:text-stone-400">Skopiuj SMS-a z listą i wklej go tutaj. Resztą zajmie się Odhaczka.</p>
      </div>
      <Link to="/new" className={`${primaryBtn} mt-2 min-h-14 text-lg`}>
        Wklej listę zakupów
      </Link>
    </div>
  );
}

interface CardProps {
  list: ListRow;
  storeName: string | null;
  progress: Progress | undefined;
  /** Kto robi teraz zakupy z tą listą (na żywo). */
  shoppers: ShoppingNow[];
  onMenu: () => void;
}

function ListCard({ list, storeName, progress, shoppers, onMenu }: CardProps) {
  const longPress = useLongPress(onMenu);
  const active = isActive(list);
  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const finished = !active && list.finishedMs ? shortDate(list.finishedMs) : null;
  const live = presenceText(shoppers);

  return (
    <li className="relative">
      <Link
        to={active ? `/lists/${list.id}` : `/lists/${list.id}/after`}
        {...longPress}
        className={`${cardCls} block select-none p-4 pr-14 [-webkit-touch-callout:none] active:bg-stone-50 dark:active:bg-stone-800 ${
          live ? 'ring-2 ring-brand-500/30' : ''
        }`}
      >
        <div className="truncate font-semibold">{list.title || 'Bez nazwy'}</div>
        {live ? (
          <div className="mt-0.5 flex items-center gap-2 text-sm font-medium text-brand-700 dark:text-brand-500">
            <LiveDot />
            <span className="min-w-0 truncate">{live}</span>
          </div>
        ) : (
          <div className="mt-0.5 flex items-center gap-2 text-sm text-stone-500">
            <span className="min-w-0 truncate">
              {storeName ?? 'bez sklepu'}
              {finished && ` · ${finished}`}
            </span>
            {!active && progress && progress.missing > 0 && (
              <span className="shrink-0 font-medium text-miss-600">
                {progress.missing} {braki(progress.missing)}
              </span>
            )}
            <span className="ml-auto shrink-0 tabular-nums">{progress ? `${progress.done}/${progress.total}` : '…'}</span>
          </div>
        )}
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-100 dark:bg-stone-800">
          <div className="h-full rounded-full bg-brand-600 transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      </Link>
      <button
        type="button"
        aria-label="Więcej opcji"
        onClick={onMenu}
        className="absolute top-1/2 right-1 flex size-12 -translate-y-1/2 items-center justify-center rounded-full text-xl text-stone-500 active:bg-stone-100 dark:active:bg-stone-800"
      >
        ⋯
      </button>
    </li>
  );
}

function ListMenu({ list, onClose, onAddItems }: { list: ListRow; onClose: () => void; onAddItems: () => void }) {
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(list.title ?? '');

  if (renaming)
    return (
      <form
        onSubmit={async (e: { preventDefault(): void }) => {
          e.preventDefault();
          await renameList(list.id, title);
          onClose();
        }}
        className="flex flex-col gap-3"
      >
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Nazwa listy"
          enterKeyHint="done"
          className={inputCls}
        />
        <button disabled={!title.trim()} className={primaryBtn}>
          Zapisz
        </button>
      </form>
    );

  const remove = async () => {
    if (!confirm(`Usunąć listę „${list.title || 'Bez nazwy'}”? Tego nie da się cofnąć.`)) return;
    await deleteList(list.id);
    onClose();
  };

  return (
    <div className="-mx-1 flex flex-col">
      {isActive(list) && <SheetAction onClick={onAddItems}>Dopisz pozycje…</SheetAction>}
      <SheetAction
        hint="Pozycje do poprawienia przed zapisaniem"
        onClick={async () => navigate('/new', { state: await newListFrom(list) })}
      >
        Nowa lista na podstawie tej
      </SheetAction>
      <SheetAction onClick={() => setRenaming(true)}>Zmień nazwę</SheetAction>
      {list.status === 'archived' ? (
        <SheetAction
          hint="Wróci do zakończonych"
          onClick={async () => {
            await unarchiveList(list.id);
            onClose();
          }}
        >
          Przywróć z archiwum
        </SheetAction>
      ) : (
        <SheetAction
          hint="Schowa się w archiwum"
          onClick={async () => {
            await archiveList(list.id);
            onClose();
          }}
        >
          Archiwizuj
        </SheetAction>
      )}
      <SheetAction tone="danger" onClick={remove}>
        Usuń
      </SheetAction>
    </div>
  );
}

/** „Bartek w Auchan · 12/30 · 2 braki”; dla siebie „Ty w …”. Null, gdy nikt nie robi zakupów. */
function presenceText(shoppers: ShoppingNow[]): string | null {
  const first = shoppers[0];
  if (!first) return null;
  const byStore = new Map<string, string[]>();
  for (const s of shoppers) byStore.set(s.storeName, [...(byStore.get(s.storeName) ?? []), s.isMe ? 'Ty' : s.name]);
  const who = [...byStore].map(([store, names]) => `${names.join(' i ')} ${store ? `w ${store}` : 'na zakupach'}`).join(', ');
  const missing = first.missing > 0 ? ` · ${first.missing} ${braki(first.missing)}` : '';
  return `${who} · ${first.done}/${first.total}${missing}`;
}

const shortDate = (ms: number) => new Date(ms).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });

function braki(n: number): string {
  if (n === 1) return 'brak';
  const d = n % 10;
  const t = n % 100;
  return d >= 2 && d <= 4 && (t < 12 || t > 14) ? 'braki' : 'braków';
}
